import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import { IFitmentOptionsDetails } from "api/interfaces/get-page-details/common/category-page.type";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import { RootState } from "interfaces/generics.type";
import ProductFitmentBox from "modules/fitment/ProductFitmentBox";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { CUSTOM_SIZES_WARNINGS } from "modules/category/constants/customSizesConstants";
import { VEHICLE_FITMENT } from "../../constant/shared-constants";
import { validateVehicle } from "../../util/vehicle/validate-vehicle";
import styles from "./productFitment.module.scss";

const ProductFitmentUnknown = dynamic(() => import(/* webpackChunkName: "pf-unknown-desktop" */ "./ProductFitmentUnknown"), { ssr: false });
const ProductFitmentUniversal = dynamic(() => import(/* webpackChunkName: "pf-universal-desktop" */ "./ProductFitmentUniversal"), { ssr: false });

const initialState = {
  vehicle: undefined,
  fittingVehicle: false,
  loading: true,
  fitmentStatus: "",
};

interface IProductFitment {
  product: ICompactProduct;
  inlineVehicleSelector?: boolean;
  showHeading?: boolean;
  showNewVehicleSelectorPart?: boolean;
  className?: string;
  iconSize?: string;
  fromSideMenu?: boolean;
  formView?: "horizontalView" | "verticalView";
  isCompactFitment?: boolean;
  showChangeVehicleButtonOnSuccess?: boolean;
  fitmentOptionsDetails: IFitmentOptionsDetails;
  showMore?: boolean;
  isLongErrMsg?: boolean;
  isHorizontalVehicleSelector?: boolean;
}

function ProductFitment(props: IProductFitment) {
  const {
    product, inlineVehicleSelector, showHeading = true, showNewVehicleSelectorPart = false,
    className, iconSize = "big", fromSideMenu, formView, isCompactFitment,
    showChangeVehicleButtonOnSuccess, fitmentOptionsDetails = {}, validateFitment,
    showMore, hideFitment, isLongErrMsg = false, isHorizontalVehicleSelector,
  } = props;

  const { fitmentStatus, fitmentNote, remainingFitmentOptions } = product;

  const [nextAttribute, setNextAttribute] = useState();
  const [attributeOptions, setAttributeOptions] = useState();
  const [showFitmentVehicleSelector, setShowFitmentVehicleSelector] = useState(false);
  const [showNewVehicleSelector, setShowNewVehicleSelector] = useState(showNewVehicleSelectorPart);

  const { t } = useTranslation("product");
  const { selectedVehicle, isLoading: isGarageLoading, defaultValues } = useSelector((store: RootState) => store.garage);
  const [state, setState] = useState(initialState);
  const attributeTitle = fitmentOptionsDetails[remainingFitmentOptions[0]]?.title;

  useEffect(() => {
    if (!validateVehicle(selectedVehicle)) {
      // Reinitialize nextAttribute when vehicle is removed or All Vehicles is selected
      setNextAttribute(null);
      setState({ ...initialState, loading: false });
      return;
    }

    const [nextFitmentOption] = product.remainingFitmentOptions;
    if (nextFitmentOption) {
      setNextAttribute(nextFitmentOption);
      setAttributeOptions(fitmentOptionsDetails[nextFitmentOption]?.fitmentOptionValues);
    } else {
      setAttributeOptions(null);
      setNextAttribute(null);
    }

    if (fitmentStatus !== "GUARANTEED_FIT") {
      setState({
        ...initialState,
        loading: false,
        vehicle: selectedVehicle,
        fittingVehicle: false,
      });
    } else {
      updateFitment();
    }
  }, [product.category?.tier3, selectedVehicle, product.remainingFitmentOptions]);
  // remainingFitmentOptions is added in deps to delay opening vehicle selector until we get fitmentOptionsPrompt

  useEffect(() => {
    setShowFitmentVehicleSelector(false);
  }, [selectedVehicle]);

  function onCheckFitment() {
    setShowFitmentVehicleSelector(true);
  }

  function onVehicleChange() {
    setShowFitmentVehicleSelector(true);
    setShowNewVehicleSelector(true);
  }

  function updateFitment() {
    if (!(product.fitment === VEHICLE_FITMENT.UNIVERSAL)
      && validateVehicle(selectedVehicle) && fitmentStatus) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { jsonNote, htmlNote } = fitmentNote;
      // eslint-disable-next-line prefer-destructuring

      setState({
        vehicle: selectedVehicle,
        fittingVehicle: fitmentStatus === "GUARANTEED_FIT",
        // eslint-disable-next-line react/no-unused-state
        loading: false,
      });
    } else if (!isGarageLoading) {
      setState({ ...initialState, loading: false, fitmentStatus });
    } else {
      setState(initialState);
    }
  }

  const { fittingVehicle } = state;

  const productFitsInVehicle = fittingVehicle;

  const fitmentBoxProps = {
    nextAttribute,
    product,
    onCheckFitment,
    onVehicleChange,
    inlineVehicleSelector,
    showNewVehicleSelector,
    iconSize,
    defaultVehicle: defaultValues,
    fromSideMenu,
    formView,
    attributeOptions,
    showChangeVehicleButtonOnSuccess,
    fitmentOptionsDetails,
    attributeTitle,
    showFitmentVehicleSelector,
    showMore,
  };

  const compactFitmentProps = isCompactFitment ? {
    showMoreButton: fitmentNote && ((fitmentNote.jsonNote && Object.keys(fitmentNote.jsonNote).length > 0) || fitmentNote.htmlNote),
    showBorder: true,
    className: styles.compact_fitment_box,
    isCompactFitment: true,
  } : {};

  function RenderSkeleton() {
    return isCompactFitment ? (
      <Skeleton height={84} count={1} />
    ) : (
      <>
        <Skeleton height={100} count={1} />
        <Skeleton height={40} count={1} />
        <Skeleton height={40} count={1} />
      </>
    );
  }

  function renderFitmentComponent() {
    if ((state.loading)) {
      <RenderSkeleton />;
    }

    if (product.fitment === VEHICLE_FITMENT.UNKNOWN || product.fitment === VEHICLE_FITMENT.UNKNOWN_FITMENT) {
      return <ProductFitmentUnknown />;
    }
    if (product.fitment === VEHICLE_FITMENT.UNIVERSAL) {
      //  http://localhost:3000/robinair-filter-drier-rob-34724
      return (
        <ProductFitmentUniversal iconSize={iconSize} />
      );
    }
    if (product.fitment !== VEHICLE_FITMENT.VEHICLE_SPECIFIC) {
      return null;
    }

    let fitment = null;
    if (CUSTOM_SIZES_WARNINGS.includes(fitmentStatus)) {
      fitment = fitmentStatus;
    } else if (productFitsInVehicle) {
      fitment = "SUCCESS";
    } else if (!validateVehicle(selectedVehicle)) {
      fitment = "SELECT_VEHICLE_WARNING";
    } else {
      fitment = fitmentStatus === "POSSIBLE_FIT" ? "SELECT_FITMENT_WARNING" : "ERROR";
    }

    return (
      <ProductFitmentBox
        {...fitmentBoxProps}
        {...compactFitmentProps}
        fitment={fitment}
        validateFitment={validateFitment}
        hideFitment={hideFitment}
        isLongErrMsg={isLongErrMsg}
        isHorizontalVehicleSelector={isHorizontalVehicleSelector}
      />
    );
  }

  return (
    <div className={`${styles.fitment_container} ${className}`}>
      <HideIf show={showHeading}>
        <PAText
          component="span"
          fontSize={FONT_SIZE[2]}
          bold
          className={styles.fitment_title}
          color="black"
        >
          {t("Vehicle Fitment")}
        </PAText>
      </HideIf>
      {renderFitmentComponent()}
    </div>
  );
}

export default ProductFitment;
